import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../context/DataContext';
import Modal from './Modal';
import './HomeworkModal.css';

/**
 * HomeworkModal
 * Comprehensive modal for creating/editing homework with:
 * - Cascading study/year/course/module selection
 * - Text or document-based modes
 * - Rich text editing for text mode
 * - File upload for document mode
 */
const HomeworkModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  loading = false
}) => {
  const { t } = useTranslation();
  const { courses = [], studies = [], data } = useData();

  const toId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return String(value.id || value._id || '');
    return String(value);
  };

  const HOMEWORK_TIME_ZONE = 'America/Paramaribo';
  const HOMEWORK_TIME_OFFSET = '-03:00';

  const toParamariboDateTime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const withSeconds = raw.length === 16 ? `${raw}:00` : raw;
    return `${withSeconds}${HOMEWORK_TIME_OFFSET}`;
  };

  const toDateTimeLocal = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const dateParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: HOMEWORK_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
    const timeParts = new Intl.DateTimeFormat('en-GB', {
      timeZone: HOMEWORK_TIME_ZONE,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(date);

    const year = dateParts.find((part) => part.type === 'year')?.value;
    const month = dateParts.find((part) => part.type === 'month')?.value;
    const day = dateParts.find((part) => part.type === 'day')?.value;
    const hours = timeParts.find((part) => part.type === 'hour')?.value;
    const minutes = timeParts.find((part) => part.type === 'minute')?.value;

    if (!year || !month || !day || !hours || !minutes) return '';
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [form, setForm] = useState({
    title: '',
    description: '',
    selectedStudy: '',
    selectedStudyYear: '',
    selectedCohort: '',
    selectedCourse: '',
    selectedModule: '',
    contentType: 'document', // 'text' or 'document'
    content: '', // for text-based homework
    file: null,
    dueDate: '',
    isActive: true,
    allowMultipleSubmissions: false,
    maxSubmissionsPerStudent: 1
  });

  const [filePreview, setFilePreview] = useState(null);
  const [errors, setErrors] = useState({});

  // Initialize form with existing homework data
  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || '',
        description: initialData.description || '',
        selectedStudy: toId(initialData.study),
        selectedStudyYear: toId(initialData.course?.studyYear),
        selectedCohort: toId(initialData.cohort),
        selectedCourse: initialData.course?.id || initialData.course?._id || '',
        selectedModule: initialData.module?.id || initialData.module?._id || '',
        contentType: initialData.contentType || 'document',
        content: initialData.content || '',
        file: null,
        dueDate: toDateTimeLocal(initialData.dueDate),
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
        allowMultipleSubmissions: initialData.allowMultipleSubmissions || false,
        maxSubmissionsPerStudent: initialData.maxSubmissionsPerStudent || 1
      });

      if (initialData.attachment) {
        setFilePreview({
          name: initialData.attachment.originalName,
          existing: true
        });
      }
    } else {
      setForm({
        title: '',
        description: '',
        selectedStudy: '',
        selectedStudyYear: '',
        selectedCohort: '',
        selectedCourse: '',
        selectedModule: '',
        contentType: 'document',
        content: '',
        file: null,
        dueDate: '',
        isActive: true,
        allowMultipleSubmissions: false,
        maxSubmissionsPerStudent: 1
      });
      setFilePreview(null);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const studyYearOptions = useMemo(() => {
    if (!form.selectedStudy) return [];
    const selectedStudy = studies.find((study) => String(study.id || study._id) === String(form.selectedStudy));
    if (!selectedStudy) return [];
    return (selectedStudy.studyYears || []).map((year) => ({
      id: toId(year),
      label: year.name || `Jaar ${year.yearNumber || year.year || ''}`
    }));
  }, [form.selectedStudy, studies]);

  const courseOptions = useMemo(() => {
    let scopedCourses = [...courses];

    if (form.selectedStudyYear) {
      scopedCourses = scopedCourses.filter((course) => String(toId(course.studyYear)) === String(form.selectedStudyYear));
    }

    return scopedCourses
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nl'))
      .map((course) => ({
        id: course.id || course._id,
        label: `${course.code || '-'} - ${course.name || 'Onbekend vak'}`,
        modules: [
          ...(course.module ? [course.module] : []),
          ...(course.modules || [])
        ]
      }));
  }, [courses, form.selectedStudyYear]);

  const moduleOptions = useMemo(() => {
    const selected = courseOptions.find((course) => String(course.id) === String(form.selectedCourse));
    if (!selected) return [];
    const deduped = new Map();
    (selected.modules || []).forEach((mod) => {
      const id = mod?.id || mod?._id || mod;
      if (!id) return;
      if (!deduped.has(String(id))) {
        deduped.set(String(id), {
          id: String(id),
          label: `${mod?.code || 'MOD'} - ${mod?.name || 'Module'}`
        });
      }
    });
    return Array.from(deduped.values()).sort((a, b) => a.label.localeCompare(b.label, 'nl'));
  }, [courseOptions, form.selectedCourse]);

  const cohortOptions = useMemo(() => {
    const allCohorts = data?.cohorts || [];
    if (!form.selectedStudy) {
      return [...allCohorts].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nl'));
    }

    return allCohorts
      .filter((cohort) => (cohort.studies || []).some((study) => toId(study) === String(form.selectedStudy)))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nl'));
  }, [data?.cohorts, form.selectedStudy]);

  const validate = () => {
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = t('homework.titleRequired') || 'Titel is verplicht';
    }

    if (!form.selectedCourse) {
      newErrors.selectedCourse = t('homework.courseRequired') || 'Vak is verplicht';
    }

    if (form.contentType === 'text') {
      if (!form.content.trim()) {
        newErrors.content = t('homework.contentRequired') || 'Inhoud is verplicht';
      }
    } else {
      if (!form.file && !filePreview?.existing) {
        newErrors.file = t('homework.fileRequired') || 'Document is verplicht';
      }
    }

    if (form.dueDate && !isValidDate(form.dueDate)) {
      newErrors.dueDate = t('homework.invalidDate') || 'Ongeldige datum';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidDate = (dateString) => {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setErrors(prev => ({
          ...prev,
          file: t('homework.pdfOnly') || 'Alleen PDF-bestanden zijn toegestaan'
        }));
        return;
      }
      setForm(prev => ({ ...prev, file }));
      setFilePreview({ name: file.name });
      setErrors(prev => {
        const { file: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await onSubmit({
        title: form.title,
        description: form.description,
        studyId: form.selectedStudy || null,
        cohortId: form.selectedCohort || null,
        courseId: form.selectedCourse,
        moduleId: form.selectedModule || null,
        contentType: form.contentType,
        content: form.contentType === 'text' ? form.content : null,
        dueDate: toParamariboDateTime(form.dueDate),
        isActive: form.isActive,
        allowMultipleSubmissions: form.allowMultipleSubmissions,
        maxSubmissionsPerStudent: parseInt(form.maxSubmissionsPerStudent, 10) || 1,
        file: form.file
      });

      // Reset form
      setForm({
        title: '',
        description: '',
        selectedStudy: '',
        selectedStudyYear: '',
        selectedCohort: '',
        selectedCourse: '',
        selectedModule: '',
        contentType: 'document',
        content: '',
        file: null,
        dueDate: '',
        isActive: true,
        allowMultipleSubmissions: false,
        maxSubmissionsPerStudent: 1
      });
      setFilePreview(null);
      setErrors({});

      onClose();
    } catch (err) {
      console.error('Homework submission error:', err);
      setErrors(prev => ({
        ...prev,
        submit: err.message || t('common.error') || 'Er is iets misgegaan'
      }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? t('homework.editAssignment') || 'Huiswerk bewerken' : t('homework.createAssignment') || 'Huiswerk aanmaken'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="homework-modal-form">
        {/* Title */}
        <div className="form-row">
          <label>Titel *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => {
              setForm(prev => ({ ...prev, title: e.target.value }));
              setErrors(prev => {
                const { title: _, ...rest } = prev;
                return rest;
              });
            }}
            placeholder="Bijv. Opdracht 3 - Onderwijsanalyse"
            className={errors.title ? 'error' : ''}
          />
          {errors.title && <span className="error-text">{errors.title}</span>}
        </div>

        {/* Description */}
        <div className="form-row">
          <label>Korte toelichting</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Wat moeten studenten precies doen?"
            rows={3}
          />
        </div>

        <div className="form-row">
          <label>Studie (optioneel)</label>
          <select
            value={form.selectedStudy}
            onChange={(e) => {
              const nextStudyId = e.target.value;
              setForm((prev) => ({
                ...prev,
                selectedStudy: nextStudyId,
                selectedStudyYear: '',
                selectedCohort: nextStudyId ? prev.selectedCohort : '',
                selectedCourse: '',
                selectedModule: '',
              }));
            }}
          >
            <option value="">Geen studiebeperking</option>
            {studies.map((study) => (
              <option key={study.id || study._id} value={study.id || study._id}>
                {study.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label>Studiejaar (optioneel)</label>
          <select
            value={form.selectedStudyYear}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, selectedStudyYear: e.target.value, selectedCourse: '', selectedModule: '' }));
            }}
            disabled={!form.selectedStudy}
          >
            <option value="">Geen studiejaarfilter</option>
            {studyYearOptions.map((year) => (
              <option key={year.id} value={year.id}>{year.label}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label>Cohort (optioneel)</label>
          <select
            value={form.selectedCohort}
            onChange={(e) => setForm((prev) => ({ ...prev, selectedCohort: e.target.value }))}
            disabled={cohortOptions.length === 0}
          >
            <option value="">Geen cohortbeperking</option>
            {cohortOptions.map((cohort) => (
              <option key={cohort.id || cohort._id} value={cohort.id || cohort._id}>
                {cohort.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label>Vak <span className="required">*</span></label>
          <select
            value={form.selectedCourse}
            onChange={(e) => {
              const nextCourse = e.target.value;
              setForm((prev) => ({ ...prev, selectedCourse: nextCourse, selectedModule: '' }));
              setErrors(prev => {
                const { selectedCourse: _, ...rest } = prev;
                return rest;
              });
            }}
            className={errors.selectedCourse ? 'error' : ''}
            required
          >
            <option value="">Selecteer een vak</option>
            {courseOptions.map((course) => (
              <option key={course.id} value={course.id}>{course.label}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label>Module (optioneel)</label>
          <select
            value={form.selectedModule}
            onChange={(e) => setForm((prev) => ({ ...prev, selectedModule: e.target.value }))}
            disabled={!form.selectedCourse || moduleOptions.length === 0}
          >
            <option value="">Geen module</option>
            {moduleOptions.map((module) => (
              <option key={module.id} value={module.id}>{module.label}</option>
            ))}
          </select>
        </div>
        {errors.selectedCourse && <span className="error-text">{errors.selectedCourse}</span>}

        {/* Content Type Toggle */}
        <div className="form-row content-type-selector">
          <label>Opdrachtvorm</label>
          <div className="toggle-buttons">
            <button
              type="button"
              className={`toggle-btn ${form.contentType === 'text' ? 'active' : ''}`}
              onClick={() => {
                setForm(prev => ({ ...prev, contentType: 'text' }));
                setErrors(prev => {
                  const { file: _, content: __, ...rest } = prev;
                  return rest;
                });
              }}
            >
              Tekstinstructie
            </button>
            <button
              type="button"
              className={`toggle-btn ${form.contentType === 'document' ? 'active' : ''}`}
              onClick={() => {
                setForm(prev => ({ ...prev, contentType: 'document' }));
                setErrors(prev => {
                  const { file: _, content: __, ...rest } = prev;
                  return rest;
                });
              }}
            >
              PDF-opdracht
            </button>
          </div>
        </div>

        {/* Text Content Editor */}
        {form.contentType === 'text' && (
          <div className="form-row">
            <label>Opdrachttekst *</label>
            <textarea
              value={form.content}
              onChange={(e) => {
                setForm(prev => ({ ...prev, content: e.target.value }));
                setErrors(prev => {
                  const { content: _, ...rest } = prev;
                  return rest;
                });
              }}
              placeholder="Plaats hier de volledige opdrachttekst"
              rows={8}
              className={`rich-text-editor ${errors.content ? 'error' : ''}`}
            />
            {errors.content && <span className="error-text">{errors.content}</span>}
          </div>
        )}

        {/* Document Upload */}
        {form.contentType === 'document' && (
          <div className="form-row">
            <label>Kies PDF-bestand *</label>
            <div className="file-upload-section">
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={loading}
                className={errors.file ? 'error' : ''}
              />
              {filePreview && (
                <div className="file-preview">
                  <span className="file-icon">📄</span>
                  <span className="file-name">{filePreview.name}</span>
                  {!filePreview.existing && (
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => {
                        setForm(prev => ({ ...prev, file: null }));
                        setFilePreview(null);
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
            </div>
            {errors.file && <span className="error-text">{errors.file}</span>}
          </div>
        )}

        {/* Due Date */}
        <div className="form-row">
          <label>Inleverdatum</label>
          <input
            type="datetime-local"
            value={form.dueDate}
            onChange={(e) => {
              setForm(prev => ({ ...prev, dueDate: e.target.value }));
              setErrors(prev => {
                const { dueDate: _, ...rest } = prev;
                return rest;
              });
            }}
            className={errors.dueDate ? 'error' : ''}
          />
          {errors.dueDate && <span className="error-text">{errors.dueDate}</span>}
        </div>

        {/* Active */}
        <div className="form-row checkbox-row">
          <label>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
            />
            Zichtbaar voor studenten
          </label>
        </div>

        {/* Advanced Options */}
        <details className="advanced-options">
          <summary>Extra opties</summary>

          <div className="form-row checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.allowMultipleSubmissions}
                onChange={(e) => setForm(prev => ({ ...prev, allowMultipleSubmissions: e.target.checked }))}
              />
              Studenten mogen opnieuw inleveren
            </label>
          </div>

          {form.allowMultipleSubmissions && (
            <div className="form-row">
              <label>Maximaal aantal inzendingen per student</label>
              <input
                type="number"
                min="1"
                value={form.maxSubmissionsPerStudent}
                onChange={(e) => setForm(prev => ({ ...prev, maxSubmissionsPerStudent: e.target.value }))}
              />
            </div>
          )}
        </details>

        {/* Submit Error */}
        {errors.submit && (
          <div className="error-message">
            {errors.submit}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? t('common.loading') : (initialData ? t('common.save') : t('homework.create') || 'Aanmaken')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default HomeworkModal;
